// Logica pura della guida "Formalità e canali" (ricerca, filtri, evidenziazione).

export interface FormalitaItem {
  n: string;
  formalita: string;
  dl98: string;
  canale: string;
  tags: string[];
  note: string;
  /** id di una voce di `noteFinali` (formalità contrassegnata con asterisco) */
  nota?: string;
}

export interface NotaFinale {
  id: string;
  titolo: string;
  testo: string[];
  allegati: { file: string; label: string }[];
}

export interface FormalitaCategoria {
  id: string;
  num: number;
  nome: string;
  items: FormalitaItem[];
}

export interface FormalitaData {
  titolo: string;
  sottotitolo: string;
  premessa: string[];
  boxes: { title: string; body: string[] }[];
  categorie: FormalitaCategoria[];
  noteFinali?: NotaFinale[];
}

export interface Segmento {
  t: string;
  hit: boolean;
}

export type FiltroDl98 = 'all' | 'si' | 'no';

export interface Filtri {
  terms: string[];
  dl: FiltroDl98;
  tags: string[];
}

/** Minuscolo senza accenti, carattere per carattere (stessa lunghezza dell'originale). */
export const norm = (s: string): string => {
  let o = '';
  Array.from(s).forEach((ch) => { o += ch.normalize('NFD')[0].toLowerCase(); });
  return o;
};

export const termini = (q: string): string[] => norm(q).split(/\s+/).filter((t) => t.length > 1);

export const isDl98Si = (it: FormalitaItem): boolean => it.dl98.startsWith('S');

/** Divide il testo in segmenti evidenziati / non evidenziati secondo i termini cercati. */
export const segmenti = (text: string, terms: string[]): Segmento[] => {
  if (!terms.length) return [{ t: text, hit: false }];
  const n = norm(text);
  const mark = new Array(n.length).fill(false);
  terms.forEach((t) => {
    let i = n.indexOf(t);
    while (i > -1) {
      for (let k = i; k < i + t.length; k++) mark[k] = true;
      i = n.indexOf(t, i + t.length);
    }
  });
  const out: Segmento[] = [];
  let cur: Segmento | null = null;
  Array.from(text).forEach((c, i) => {
    const h = mark[i];
    if (!cur || cur.hit !== h) {
      cur = { t: '', hit: h };
      out.push(cur);
    }
    cur.t += c;
  });
  return out;
};

export const corrisponde = (c: FormalitaCategoria, it: FormalitaItem, f: Filtri): boolean => {
  if (f.dl !== 'all' && (isDl98Si(it) ? 'si' : 'no') !== f.dl) return false;
  if (f.tags.length && !f.tags.some((t) => it.tags.includes(t))) return false;
  if (f.terms.length) {
    const hay = norm(`${c.num}.${it.n} ${it.formalita} ${it.canale} ${it.note} ${c.nome}`);
    if (!f.terms.every((t) => hay.includes(t))) return false;
  }
  return true;
};

export const filtraPerCategoria = (cats: FormalitaCategoria[], f: Filtri) =>
  cats.map((c) => ({ c, items: c.items.filter((it) => corrisponde(c, it, f)) }));

/** Separa il riferimento alla circolare dal testo, per trasformarlo in link. */
export const RIF_CIRCOLARE = /(Circ\. DGMoT n\. 22022 del [0-9/]+)/;

/** Link al modulo di segnalazione (Supporto → canale Documento Unico) con oggetto precompilato. */
export const linkSegnalazione = (codice?: string, formalita?: string): string => {
  const oggetto = codice
    ? `Guida formalità e canali – ${codice}${formalita ? ` ${formalita}` : ''}`
    : 'Guida formalità e canali';
  const p = new URLSearchParams({ nuovo: '1', canale: 'documento-unico', oggetto: oggetto.slice(0, 200) });
  return `/area-riservata/supporto?${p.toString()}`;
};
